
import { mapMutations, mapGetters } from "vuex";
import mixins from "vue-typed-mixins";
import Common from "../mixins/Common";
import CustomValidation from "../mixins/CustomValidation";
import swal from "sweetalert";
import store from "@/store/index";
import { object, string, boolean, number, date } from "yup";
import { subscriptionPaymentFlowScreenView } from "@/interfaces/SubscriptionPaymentFlowEnum";
import LottieAnimation from "@/components/LottieAnimationsComponent.vue";
import { AffiliateSubscription } from "@/interfaces/AffiliateSubscription";
import { PatientAffiliateSubscription } from "@/interfaces/PatientAffiliateSubscription";
import { PatientViewModel } from "@/interfaces/PatientViewModel";
import { rxEstimateScreenView } from "@/interfaces/PharmacyCartViewEnum";
import confetti from "canvas-confetti";
import { FamilyPlanInviteViewModel } from "@/interfaces/FamilyPlanInviteViewModel";
import { FamilyUserStatusIDEnum } from "@/interfaces/FamilyUserStatusIDEnum";
import SubscriptionSelector from "@/components/MedicationMarketplaceSubscriptionSelectComponent.vue";

export interface ICollectJS {
  startPaymentRequest: (arg0: any) => void;
  configure: (arg0: any) => void;
}

export default mixins(CustomValidation, Common).extend({
  name: "SubscriptionPaymentFlow",
  props: {
    wrapperClass: {
      type: String,
      default: "",
    },
    view: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      subscriptionPaymentFlowScreenView,
      isLoadingCart: false as boolean,
      selectedSubscription: {} as AffiliateSubscription,
      isMonthly: true,
      affiliateSubscriptions: [] as AffiliateSubscription[],
      patientAffiliateSubscription: {} as PatientAffiliateSubscription,
      patient: {} as PatientViewModel,
      authToken: null as string | null,
      keepVaultID: null as boolean | null,
      secCodeError: null as string | null,
      expDateError: null as string | null,
      cardNumError: null as string | null,
      ccInfoInvalid: false,
      paymentAddressSchema: object().shape({
        homeStreetAddress: string().typeError("Address is required.").required("Address is required."),
        homeCity: string().typeError("City is required.").required("City is required."),
        selectedHomeState: string().typeError("State is required.").required("State is required."),
        homeZip: number().integer().typeError("Zip must be a number and is required.").max(99999, "Zip must be a number and is required.").required("Zip must be a number and is required."),
      }),
      homeStreetAddress: "" as string | null,
      homeStreetAddress2: "" as string | null,
      homeCity: "" as string | null,
      homeZip: "" as string | null,
      selectedHomeState: "" as string | null,
      states: [
        { name: "Alabama", abbr: "AL" },
        { name: "Alaska", abbr: "AK" },
        { name: "Arizona", abbr: "AZ" },
        { name: "Arkansas", abbr: "AR" },
        { name: "California", abbr: "CA" },
        { name: "Colorado", abbr: "CO" },
        { name: "Connecticut", abbr: "CT" },
        { name: "Delaware", abbr: "DE" },
        { name: "Florida", abbr: "FL" },
        { name: "Georgia", abbr: "GA" },
        { name: "Hawaii", abbr: "HI" },
        { name: "Idaho", abbr: "ID" },
        { name: "Illinois", abbr: "IL" },
        { name: "Indiana", abbr: "IN" },
        { name: "Iowa", abbr: "IA" },
        { name: "Kansas", abbr: "KS" },
        { name: "Kentucky", abbr: "KY" },
        { name: "Louisiana", abbr: "LA" },
        { name: "Maine", abbr: "ME" },
        { name: "Maryland", abbr: "MD" },
        { name: "Massachusetts", abbr: "MA" },
        { name: "Michigan", abbr: "MI" },
        { name: "Minnesota", abbr: "MN" },
        { name: "Mississippi", abbr: "MS" },
        { name: "Missouri", abbr: "MO" },
        { name: "Montana", abbr: "MT" },
        { name: "Nebraska", abbr: "NE" },
        { name: "Nevada", abbr: "NV" },
        { name: "New Hampshire", abbr: "NH" },
        { name: "New Jersey", abbr: "NJ" },
        { name: "New Mexico", abbr: "NM" },
        { name: "New York", abbr: "NY" },
        { name: "North Carolina", abbr: "NC" },
        { name: "North Dakota", abbr: "ND" },
        { name: "Ohio", abbr: "OH" },
        { name: "Oklahoma", abbr: "OK" },
        { name: "Oregon", abbr: "OR" },
        { name: "Pennsylvania", abbr: "PA" },
        { name: "Rhode Island", abbr: "RI" },
        { name: "South Carolina", abbr: "SC" },
        { name: "South Dakota", abbr: "SD" },
        { name: "Tennessee", abbr: "TN" },
        { name: "Texas", abbr: "TX" },
        { name: "Utah", abbr: "UT" },
        { name: "Vermont", abbr: "VT" },
        { name: "Virginia", abbr: "VA" },
        { name: "Washington", abbr: "WA" },
        { name: "West Virginia", abbr: "WV" },
        { name: "Wisconsin", abbr: "WI" },
        { name: "Wyoming", abbr: "WY" },
        { name: "District of Columbia", abbr: "DC" },
      ],
      orderID: "" as string | null,
      isFailedPaymentUpdate: false as boolean,
      familyPlanDetails: {} as FamilyPlanInviteViewModel,
      respondingToFamPlan: false,
    };
  },
  computed: {
    ...mapGetters({
      ShowPharmacyCart: "getShowPharmacyCart",
      MarketplaceEstimates: "getMarketplaceEstimates",
      SignedIn: "getSignedIn",
      Content: "getContent",
      Affiliate: "getAffiliate",
      MarketplaceRxs: "getMarketplaceRxs",
      Theme: "getTheme",
      IsCollectJSLoaded: "getIsCollectJSLoaded",
      SubscriptionPaymentFlowView: "getSubscriptionPaymentFlowView",
      PatientAffiliateSubscriptionUpdate: "getPatientAffiliateSubscriptionUpdate",
      ForceSubscriptionFlow: "getForceSubscriptionFlow",
    }),
    paddedOrderID(): string {
      return this.orderID.padStart(8, "0");
    },
  },
  watch: {
    view(newVal) {
      if (newVal === subscriptionPaymentFlowScreenView.checkout) {
        this.setupValidation(this.paymentAddressSchema);
        this.setupCollect();
        window.setInterval(() => {
          this.ccInfoInvalidChecker();
        }, 1000);
      }
    },
  },
  created() {
    this.getAffiliateSubscriptionData();
    this.setupValidation(this.paymentAddressSchema);
    this.setupCollect();
    window.setInterval(() => {
      this.ccInfoInvalidChecker();
    }, 1000);
    this.getPendingFamilyPlanInvite();
  },
  methods: {
    ...mapMutations({
      setLoading: "setLoading",
      setShowSubscriptionPaymentFlow: "setShowSubscriptionPaymentFlow",
      setShowPharmacyCart: "setShowPharmacyCart",
      setPatientAffiliateSubscription: "setPatientAffiliateSubscription",
    }),
    isBillingMonthly(isMonthly: boolean) {
      this.isMonthly = isMonthly;
    },
    getLocalPatientData() {
      this.patient.homeStreetAddress1 = this.homeStreetAddress;
      this.patient.homeStreetAddress2 = this.homeStreetAddress2;
      this.patient.homeCity = this.homeCity;
      this.patient.homeState = this.selectedHomeState;
      this.patient.homeZipCode = this.homeZip;
    },
    continueFlow() {
      const selected = this.affiliateSubscriptions.find((s) => s === this.selectedSubscription);
      if (selected && selected.monthlyPrice === 0 && selected.yearlyPrice === 0) {
        this.setShowSubscriptionPaymentFlow({ show: true, view: subscriptionPaymentFlowScreenView.selectProgramVerify, forceFlow: this.ForceSubscriptionFlow });
      } else {
        this.setShowSubscriptionPaymentFlow({ show: true, view: this.SubscriptionPaymentFlowView + 1, forceFlow: this.ForceSubscriptionFlow });
        this.$nextTick(() => {
          if (this.view === subscriptionPaymentFlowScreenView.checkout) {
            if (this.$refs.checkout) {
              const checkoutElement = this.$refs.checkout as HTMLElement;
              checkoutElement.scrollIntoView({ behavior: "smooth" });
            }
          }
          if (this.view === subscriptionPaymentFlowScreenView.success) {
            if (this.$refs.checkoutSuccess) {
              const checkoutSuccessElement = this.$refs.checkoutSuccess as HTMLElement;
              checkoutSuccessElement.scrollIntoView({ behavior: "smooth" });
            }
          }
        });
      }
    },

    // Patients will only hit this if they choose a free subscription or accept a family plan invitation
    async backToOriginalSignUp(skipSave = false): Promise<void> {
      this.setLoading(true);

      if (!skipSave) {
        let response = await store.dispatch("SaveMarketplaceSubscriptionPayment", {
          affiliateSubscription: this.selectedSubscription,
          isMonthly: this.isMonthly,
          authToken: this.authToken,
        });

        if (!response.success) {
          swal("Error", "Error updating subscription. Please try again.", "error");
          this.setLoading(false);
          return;
        }

        this.setPatientAffiliateSubscription(response.data.patientAffiliateSubscription);
      }

      // If patient has not entered basic info, make them do it now
      if (this.patient.gender == null || this.patient.dateOfBirth == null || this.patient.state == null || this.patient.phone == null) {
        this.setShowSubscriptionPaymentFlow({ show: false, view: subscriptionPaymentFlowScreenView.selectProgram, forceFlow: false });
        this.setShowPharmacyCart({ show: true, type: "rxEstimate", view: rxEstimateScreenView.basicinfo });
        this.setLoading(false);
        return;
      }

      this.setShowSubscriptionPaymentFlow({ show: false, view: subscriptionPaymentFlowScreenView.selectProgram, forceFlow: false });
      this.setShowPharmacyCart({ show: false, type: "", view: rxEstimateScreenView.basicinfo });
      window.location.reload();
    },

    backToPatientPortal() {
      // This conditon is for the original signup flow from medication marketplace if a user selected a paid plan
      if (this.patient.gender == null || this.patient.dateOfBirth == null || this.patient.state == null || this.patient.phone == null) {
        this.setShowSubscriptionPaymentFlow({ show: false, view: subscriptionPaymentFlowScreenView.selectProgram, forceFlow: false });
        this.setShowPharmacyCart({ show: true, type: "rxEstimate", view: rxEstimateScreenView.basicinfo });
      } else {
        this.setLoading(true);
        this.setShowSubscriptionPaymentFlow({ show: false, view: subscriptionPaymentFlowScreenView.selectProgram, forceFlow: false });
        this.setShowPharmacyCart({ show: false, type: "", view: rxEstimateScreenView.basicinfo });
        window.location.reload();
      }
    },

    async getAffiliateSubscriptionData() {
      const result = await this.$store.dispatch("GetAffiliateSubscriptionData");
      if (!result) {
        return;
      }
      this.affiliateSubscriptions = result.data.affiliateSubscriptions;
      this.patientAffiliateSubscription = result.data.patientAffiliateSubscription;
      this.patient = result.data.patient;
      this.homeStreetAddress = this.patient.homeStreetAddress1;
      this.homeStreetAddress2 = this.patient.homeStreetAddress2;
      this.homeCity = this.patient.homeCity;
      this.selectedHomeState = this.patient.homeState;
      this.homeZip = this.patient.homeZipCode;
      this.keepVaultID = result.data.patient.vaultID != null;

      // Add a check here to see if PatientAffiliateSubscriptionUpdate is empty.
      // If it's not then `this.selectedSubscription` needs to be set to the
      // correct AffilateSubscription object that matches the ID property and
      // isMontly needs set to whatever what isMontly property is in the object.

      if (this.PatientAffiliateSubscriptionUpdate && Object.keys(this.PatientAffiliateSubscriptionUpdate).length > 0) {
        const matchedSub = this.affiliateSubscriptions.find((sub) => sub.id === this.PatientAffiliateSubscriptionUpdate.affiliateSubscriptionID);

        if (matchedSub) {
          this.selectedSubscription = matchedSub;
          this.isMonthly = this.PatientAffiliateSubscriptionUpdate.isMonthly;
          this.isFailedPaymentUpdate = this.PatientAffiliateSubscriptionUpdate.isFailedPaymentUpdate;
          this.keepVaultID = false;
        }
      }
    },
    backToSelectSubscription() {
      this.setShowSubscriptionPaymentFlow({ show: true, view: subscriptionPaymentFlowScreenView.selectProgram, forceFlow: this.ForceSubscriptionFlow });
    },

    // NMI
    async setupCollect() {
      if ((this.authToken != null && this.authToken.length > 0) || this.keepVaultID) {
        this.setLoading(false);
        return false;
      }
      while (!(window as any).CollectJS) {
        await this.sleep(100);
      }

      while (!document.getElementById("cardNum") || !document.getElementById("expDate") || !document.getElementById("secCode")) {
        await this.sleep(100);
      }

      const CollectJS: ICollectJS = (window as any).CollectJS;
      CollectJS.configure({
        paymentSelector: "#customPayButton",
        styleSniffer: "true",
        customCss: {
          display: "block",
          width: "100%",
          height: "45px",
          padding: "0.375rem 0.75rem",
          "font-Size": "14px",
          "font-weight": "400",
          "line-height": "1.5",
          color: (this.Theme.Colors as { Key: string; Value: string }[]).find((x) => x.Key == "--m-text-color")?.Value,
          "background-color": (this.Theme.Colors as { Key: string; Value: string }[]).find((x) => x.Key == "--m-background")?.Value,
          "background-clip": "padding-box",
          border: "1px solid #ced4da",
          "border-radius": "0.25rem",
          transition: "border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
        },
        fields: {
          ccnumber: {
            selector: "#cardNum",
            title: "Card Number",
            placeholder: "Credit Card Number",
          },
          ccexp: {
            selector: "#expDate",
            title: "Card Expiration",
            placeholder: "Exp. Date (MM/YY)",
          },
          cvv: {
            display: "show",
            selector: "#secCode",
            title: "CVV Code",
            placeholder: "CVV",
          },
        },
        variant: "inline",
        callback: async (response: { token: any }) => {
          console.log("CollectJS callback received response:", response);

          this.authToken = response.token;
          console.log(`this.authToken ${this.authToken}`);
          this.setLoading(false);

          let action = this.isFailedPaymentUpdate ? "UpdateMarketplaceSubscriptionPayment" : "SaveMarketplaceSubscriptionPayment";

          let response2 = await store.dispatch(action, {
            affiliateSubscription: this.selectedSubscription,
            isMonthly: this.isMonthly,
            authToken: this.authToken,
          });

          if (!response2.success) {
            swal("Error", "Error during checkout. Please try again.", "error");
            this.isLoadingCart = false;
            return;
          }

          this.orderID = response2.data.orderID;
          this.setPatientAffiliateSubscription(response2.data.patientAffiliateSubscription);
          this.continueFlow();
          this.isLoadingCart = false;
        },
        validationCallback: (field: string, status: string, message: string) => {
          if (status) {
            switch (field) {
              case "ccnumber":
                this.cardNumError = null;
                break;
              case "ccexp":
                this.expDateError = null;
                break;
              case "cvv":
                this.secCodeError = null;
                break;
              default:
            }
          } else {
            switch (field) {
              case "ccnumber":
                this.cardNumError = message;
                break;
              case "ccexp":
                this.expDateError = message;
                break;
              case "cvv":
                this.secCodeError = message;
                break;
              default:
            }
            this.setLoading(false);
          }
        },
        timeoutDuration: 10000,
        timeoutCallback: () => {
          this.setLoading(false);
          swal("", "There was an error processing your credit card. Please try again. If you continue to experience this issue, please contact us.", "warning");
        },
        fieldsAvailableCallback: () => {
          this.setLoading(false);
        },
      });
      this.setLoading(false);
    },
    ccInfoInvalidChecker() {
      if (document.querySelectorAll(".CollectJSInlineIframe.CollectJSValid").length < 3) {
        this.ccInfoInvalid = true;
      } else {
        this.ccInfoInvalid = false;
      }
    },
    waitCCInfoValid(successCB: () => void, failureCB: () => void, retries: number) {
      window.setTimeout(() => {
        retries--;
        if (!this.ccInfoInvalid) {
          successCB();
        } else {
          if (retries > 0) {
            this.waitCCInfoValid(successCB, failureCB, retries);
          } else {
            failureCB();
          }
        }
      }, 500);
    },
    btnClearCreditCard_Click() {
      this.authToken = null;
      this.keepVaultID = false;
      this.setupCollect();
    },

    // Checkout
    async savePaymentAddress() {
      this.errors = [];
      try {
        this.errors = [];

        this.getLocalPatientData();

        await this.schema.validate(this, { abortEarly: false });

        let response = await this.$store.dispatch("SaveMarketplacePatient", this.patient);
        if (!response) {
          throw new Error("Error updating address. Please try again.");
        }
      } catch (err) {
        this.isLoadingCart = false;

        if (err.inner) {
          err.inner.forEach((error: { path: string; message: string }): void => {
            this.errors.push({ key: error.path, value: error.message });
          });
        }

        throw err;
      }
    },
    async checkout() {
      this.isLoadingCart = true;
      try {
        await this.savePaymentAddress();

        const CollectJS: ICollectJS = (window as any).CollectJS;

        if ((this.authToken != null && this.authToken.length > 0) || this.keepVaultID) {
          // already have cc info
          console.log("already have cc info");

          let action = this.isFailedPaymentUpdate ? "UpdateMarketplaceSubscriptionPayment" : "SaveMarketplaceSubscriptionPayment";

          let response2 = await store.dispatch(action, {
            affiliateSubscription: this.selectedSubscription,
            isMonthly: this.isMonthly,
            authToken: this.authToken,
          });

          if (!response2.success) {
            swal("Error", "Error during checkout. Please try again.", "error");
            this.isLoadingCart = false;
            return;
          }

          this.orderID = response2.data.orderID;
          this.setPatientAffiliateSubscription(response2.data.patientAffiliateSubscription);
          this.continueFlow();
          this.isLoadingCart = false;
          confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 },
          });
          return;
        }

        this.waitCCInfoValid(
          () => {
            // Success need to get cc info
            console.log("Success need to get cc info");
            console.log(event);
            CollectJS.startPaymentRequest(event);
          },
          () => {
            // Failure in cc info
            console.log("Failure in cc info");
            this.isLoadingCart = false;
            swal("", "Valid credit card information is required.", "warning");
          },
          6
        );
      } catch (error: any) {
        this.isLoadingCart = false;
        swal("Error", "Error during checkout. Please try again.", "error");
      }
    },

    async getPendingFamilyPlanInvite() {
      try {
        const response = await this.$store.dispatch("GetPendingFamilyPlanRequest");

        if (response && response.data) {
          this.familyPlanDetails = response.data;
        }
      } catch (error) {
        swal("Error", "Error loading family plan invite. Please try again.", "error");
      }
    },

    async confirmFamilyPlanResponse(message: string): Promise<boolean> {
      return swal({
        title: "Are you sure?",
        text: message,
        icon: "warning",
        buttons: ["Cancel", "Yes"],
        dangerMode: true,
      });
    },

    async handleFamilyPlanResponse(accepted: boolean) {
      if (this.respondingToFamPlan) return;
      this.respondingToFamPlan = true;

      const action = accepted ? "ACCEPT" : "DENY";
      const name = `${this.familyPlanDetails.mainUserFirstName} ${this.familyPlanDetails.mainUserLastName}`;
      const confirmed = await this.confirmFamilyPlanResponse(`Are you sure that you want to ${action} the family plan invitation from ${name}?`);

      if (!confirmed) {
        this.respondingToFamPlan = false;
        return;
      }

      try {
        // Update the enum value based off of user's response
        this.familyPlanDetails.familyUserStatusID = accepted ? FamilyUserStatusIDEnum.Accepted : FamilyUserStatusIDEnum.Denied;

        const response = await this.$store.dispatch("FamilyPlanInvitationConfirmation", this.familyPlanDetails);

        if (response.data && response.data.success) {
          swal("Success", `You have ${accepted ? "accepted" : "denied"} the invitation.`, "success");
          if (accepted) {
            this.backToOriginalSignUp(true);
          } else {
            this.setShowSubscriptionPaymentFlow({ show: true, view: subscriptionPaymentFlowScreenView.selectProgram, forceFlow: false });
          }
        } else {
          swal("Error", "An error occurred. Please try again.", "error");
          this.respondingToFamPlan = false;
        }
      } catch (error) {
        swal("Error", "Unable to send your response. Please try again.", "error");
      } finally {
        this.respondingToFamPlan = false;
      }
    },
  },
  components: {
    LottieAnimation,
    SubscriptionSelector,
  },
});
